import React, { useState, useEffect } from 'react';
import { PMT2Product } from '@/types/pmt2Types';
import { pmt2Service, SerpRanksData, SerpRankEntry } from '@/services/pmt2Service';
import { Link as LinkIcon } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';

interface SerpRanksDashboardProps {
  product: PMT2Product | null;
}

const SerpRanksDashboard: React.FC<SerpRanksDashboardProps> = ({ product }) => {
  const [serpData, setSerpData] = useState<SerpRanksData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!product) {
        setSerpData(null);
        setError(null);
        return setLoading(false);
      }

      try {
        setLoading(true);
        const data = await pmt2Service.getSerpRanksData(product.companyName, product.id);
        setSerpData(data);
        setError(null);
      } catch (err) {
        console.error('[SerpRanksDashboard] Error fetching SERP ranks:', err);
        setError('Failed to load SERP Ranks data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [product]);

  const formatCurrency = (value: any): string => {
    const num = parseFloat(value);
    if (isNaN(num)) return '-';
    return `$${Math.round(num)}`;
  };

  const formatRank = (value: any): string => {
    if (value === null || value === undefined) return '-';
    return `${value}`;
  };

  if (!product) return null;

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>SERP Ranks</CardTitle>
        </CardHeader>
        <CardContent className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-white"></div>
          <span className="ml-2">Loading SERP Ranks...</span>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>SERP Ranks</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4 rounded-md text-red-700 dark:text-red-300">
            {error}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          SERP Ranks
          {serpData?.generatorUrl && (
            <a href={serpData.generatorUrl} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">
              <LinkIcon size={18} />
            </a>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {serpData && serpData.ranks.length > 0 ? (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Term</TableHead>
                  <TableHead>PPC Sales</TableHead>
                  <TableHead>Week 0</TableHead>
                  <TableHead>Week -1</TableHead>
                  <TableHead>Week -2</TableHead>
                  <TableHead>Week -3</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {serpData.ranks
                  .slice()
                  .sort((a, b) => {
                    const aSales = typeof a.ppcSales === 'number' ? a.ppcSales : -Infinity;
                    const bSales = typeof b.ppcSales === 'number' ? b.ppcSales : -Infinity;
                    return bSales - aSales; // Descending order
                  })
                  .map((entry, idx) => (
                    <TableRow key={idx} className="py-1">
                      <TableCell className="py-1">{entry.term}</TableCell>
                      <TableCell className="py-1">{formatCurrency(entry.ppcSales)}</TableCell>
                      <TableCell className="py-1">{formatRank(entry.rank_week_0)}</TableCell>
                      <TableCell className="py-1">{formatRank(entry.rank_week_1)}</TableCell>
                      <TableCell className="py-1">{formatRank(entry.rank_week_2)}</TableCell>
                      <TableCell className="py-1">{formatRank(entry.rank_week_3)}</TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <p className="text-gray-500 dark:text-gray-400">No SERP Rank data available for this product.</p>
        )}
      </CardContent>
    </Card>
  );
};

export default SerpRanksDashboard;